import { Controller } from "@hotwired/stimulus";
import {
  consecutiveDateGroups,
  rowDateValues,
} from "../helpers/line_item_form.js";

export default class extends Controller {
  static targets = [
    "sections",
    "emptyState",
    "quantityLabel",
    "suppliesSection",
  ];
  static values = { units: Object, labels: Object };

  connect() {
    this.#toggleVisibility();
    this.#updateQuantityLabel();
    this.updateSummaries();
  }

  taskSelected(event) {
    // hw-combobox:selection fires with event.detail.value
    // Only show sections if a value was actually selected
    if (!event?.detail?.value) return;

    this.#toggleVisibility();
    this.#updateQuantityLabel();
    this.updateSummaries();
    this.#loadSuppliesForTask();
    this.#notifyFieldTaskChanged();
  }

  // new-field-task:change: a typed Field Task the Farm doesn't have yet (ADR 0090) counts as selected, with its unit
  // and cost meta; null when an existing task is picked.
  newFieldTaskChanged(event) {
    const hadNewTask = Boolean(this.newTask);
    this.newTask = event.detail;
    if (!hadNewTask && !this.newTask) return;

    this.#toggleVisibility();
    this.#updateQuantityLabel();
    this.updateSummaries();
    this.#notifyFieldTaskChanged();
  }

  taskRemoved() {
    this.#toggleVisibility();
    this.#clearSupplies();
    this.#notifyFieldTaskChanged();
  }

  #notifyFieldTaskChanged() {
    const rowsEl = this.element.querySelector(
      "[data-controller~='line-item-rows']",
    );
    if (!rowsEl) return;
    rowsEl.dispatchEvent(
      new CustomEvent("field-task-changed", { bubbles: true, detail: this.newTask || null }),
    );
  }

  // Keeps every row's amount labels in step with the selected dates, e.g. "Jornales (total)".
  updateSummaries() {
    const rows = this.element.querySelectorAll(
      "[data-line-item-rows-target='row']",
    );

    rows.forEach((row) => {
      this.#updateAmountLabels(row, rowDateValues(row, this.element));
    });
  }

  // Deferred version for quick-select buttons that update checkboxes
  // in a sibling controller — ensures we read after those changes land.
  updateSummariesDeferred() {
    requestAnimationFrame(() => this.updateSummaries());
  }

  #consecutiveDateGroups(dateValues) {
    return consecutiveDateGroups(dateValues);
  }

  #updateAmountLabels(row, dateValues) {
    const quantityLabel = row.querySelector(
      "[data-line-item-form-target~='quantityLabel']",
    );
    if (quantityLabel) {
      const unitData = this.#currentUnitData();
      const baseLabel =
        unitData?.other || quantityLabel.dataset.default || "Cant";
      quantityLabel.textContent = this.#labelWithScope(baseLabel, dateValues);
    }

    const laborUnitsLabel = row.querySelector("[data-labor-units-label]");
    if (laborUnitsLabel) {
      const baseLabel = laborUnitsLabel.dataset.default || laborUnitsLabel.textContent;
      laborUnitsLabel.textContent = this.#labelWithScope(baseLabel, dateValues);
    }
  }

  #labelWithScope(baseLabel, dateValues) {
    const suffix = this.#amountScopeSuffix(dateValues);
    if (!suffix) return baseLabel;

    return `${baseLabel} (${suffix})`;
  }

  #amountScopeSuffix(dateValues) {
    const dateGroups = this.#consecutiveDateGroups(dateValues);
    if (dateGroups.length === 0) return "";

    const l = this.labelsValue;
    return dateGroups.length === 1
      ? l.labelScopeTotal || "total"
      : l.labelScopeEachTask || "each task";
  }

  #hasTaskSelected() {
    const hiddenInput = this.element.querySelector(
      'input[name="field_cycle_line_item[field_task_id]"]',
    );
    return hiddenInput?.value?.trim().length > 0 || Boolean(this.newTask);
  }

  #toggleVisibility() {
    const hasTask = this.#hasTaskSelected();

    if (this.hasSectionsTarget) {
      this.sectionsTarget.classList.toggle("hidden", !hasTask);
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", hasTask);
    }
  }

  #updateQuantityLabel() {
    if (!this.hasQuantityLabelTarget) return;

    const unitData = this.#currentUnitData();
    this.quantityLabelTargets.forEach((label) => {
      if (unitData?.other) {
        label.textContent = unitData.other;
      } else {
        label.textContent = label.dataset.default || "Cant";
      }
    });
  }

  #currentUnitData() {
    if (this.newTask) return { one: this.newTask.unitOne, other: this.newTask.unitOther };

    const hiddenInput = this.element.querySelector(
      'input[name="field_cycle_line_item[field_task_id]"]',
    );
    if (!hiddenInput?.value) return null;
    return this.unitsValue[hiddenInput.value] || null;
  }

  lotsChanged() {
    this.#refreshSupplyLots();
  }

  #loadSuppliesForTask() {
    if (!this.hasSuppliesSectionTarget) return;

    const hiddenInput = this.element.querySelector(
      'input[name="field_cycle_line_item[field_task_id]"]',
    );
    if (!hiddenInput?.value) return;

    const supplyRowsController = this.#supplyRowsController();
    if (supplyRowsController) {
      supplyRowsController.loadDefaults(hiddenInput.value);
    }
  }

  #refreshSupplyLots() {
    const supplyRowsController = this.#supplyRowsController();
    if (supplyRowsController) supplyRowsController.refreshLots();
  }

  #supplyRowsController() {
    if (!this.hasSuppliesSectionTarget) return null;

    return this.application.getControllerForElementAndIdentifier(
      this.suppliesSectionTarget,
      "supply-rows",
    );
  }

  #clearSupplies() {
    const supplyRowsController = this.#supplyRowsController();
    if (supplyRowsController) {
      supplyRowsController.loadDefaults(null);
    }
  }
}
